import React from 'react';
import { Grid, Card, CardActionArea, Box, Typography } from '@mui/material';

/**
 * Calcula la luminancia relativa de un color según WCAG
 * @param {number} r - Componente rojo (0-255)
 * @param {number} g - Componente verde (0-255)
 * @param {number} b - Componente azul (0-255)
 * @returns {number} Luminancia relativa (0-1)
 */
const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map(val => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calcula el ratio de contraste entre dos colores
 * @param {string} color1 - Color en formato HEX
 * @param {string} color2 - Color en formato HEX
 * @returns {number} Ratio de contraste
 */
const getContrastRatio = (color1, color2) => {
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 1;

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Determina el color de texto (blanco o negro) que tiene mejor contraste con el color de fondo
 * @param {string} backgroundColor - Color de fondo en formato HEX
 * @returns {string} 'white' o 'black'
 */
const getContrastTextColor = (backgroundColor) => {
  if (!backgroundColor) return 'inherit';
  
  // Asegurar que el color tenga el formato correcto
  const hexColor = backgroundColor.startsWith('#') ? backgroundColor : `#${backgroundColor}`;
  
  // Calcular contraste con blanco y negro
  const contrastWithWhite = getContrastRatio(hexColor, '#FFFFFF');
  const contrastWithBlack = getContrastRatio(hexColor, '#000000');
  
  // Devolver el color con mayor contraste
  return contrastWithWhite > contrastWithBlack ? 'white' : 'black';
};

const GridSelector = ({ items, onSelect }) => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No hay categorías disponibles
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid item xs={6} sm={4} md={3} key={item.id}>
          <Card 
            sx={{ 
              height: 140, 
              display: 'flex', 
              flexDirection: 'column',
              backgroundColor: item.color || 'grey.200',
              '&:hover': {
                opacity: 0.9,
                transform: 'scale(1.02)',
                transition: 'all 0.2s ease-in-out',
              }
            }}
          >
            <CardActionArea
              onClick={() => onSelect(item)}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: 2,
                backgroundColor: 'transparent',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: item.color ? getContrastTextColor(item.color) : 'text.primary',
                  fontWeight: 'bold',
                  textShadow: item.color && getContrastTextColor(item.color) === 'white' 
                    ? '0 1px 2px rgba(0,0,0,0.2)' 
                    : 'none',
                }}
              >
                {item.alias || item.name}
              </Typography>
            </CardActionArea>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default GridSelector;
